import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Coach Drill Library.
 *
 * Browses the platform's public starter drills + the coach's own
 * private drills. Coaches can:
 *   - filter by focus tag / role / age group,
 *   - create a private drill,
 *   - assign any drill as a Task to one of their athletes.
 *
 * Free athletes can read the library too (so it doubles as a learning
 * surface), but the assign-to-athlete flow is coach-only.
 */
export default function DrillLibrary() {
  const { token, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [drills, setDrills] = useState([]);
  const [filter, setFilter] = useState({ focus_tag: '', role: '', age_group: '' });
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const isCoach = user?.cricket_role === 'coach' || user?.cricket_role === 'Coach';

  const load = async () => {
    const params = {};
    if (filter.focus_tag) params.focus_tag = filter.focus_tag;
    if (filter.role) params.role = filter.role;
    if (filter.age_group) params.age_group = filter.age_group;
    try {
      const r = await axios.get(`${API}/coach/drills`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrills(r.data.drills || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load drills');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, token]);

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-5xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="opacity-70 hover:opacity-100">← Back</button>
        <Logo size={28} />
        <div className="flex-1" />
        {isCoach && (
          <button
            onClick={() => setCreating(true)}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
          >
            + New drill
          </button>
        )}
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Drill library</h1>
          <p className="text-sm opacity-70 mt-1">
            Starter drills from Cricket Refreshed + your own.
          </p>
        </div>

        <Filters value={filter} onChange={setFilter} />

        <div className="grid md:grid-cols-2 gap-4">
          {drills.map((d) => (
            <DrillCard
              key={d.id} drill={d} theme={theme}
              showAssign={isCoach}
              onAssign={() => setAssigning(d)}
            />
          ))}
        </div>
      </main>

      {creating && (
        <CreateDrillModal
          token={token}
          onClose={() => { setCreating(false); load(); }}
          theme={theme}
        />
      )}

      {assigning && (
        <AssignTaskModal
          drill={assigning} token={token}
          onClose={() => setAssigning(null)}
          theme={theme}
        />
      )}
    </div>
  );
}

function Filters({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {[
        ['focus_tag', 'Focus', ['', 'cover_drive', 'defence', 'bowling_accuracy', 'fielding', 'fitness', 'mental']],
        ['role',      'Role',  ['', 'Batter', 'Bowler', 'All-Rounder', 'Wicket-Keeper']],
        ['age_group', 'Age',   ['', 'U11', 'U13', 'U15', 'U17', 'Adult']],
      ].map(([key, label, options]) => (
        <select
          key={key}
          value={value[key]}
          onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          className="px-3 py-1.5 rounded-lg text-sm"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)',
            color: 'inherit',
          }}
        >
          {options.map((o) => (
            <option key={o} value={o}>{o ? o : `Any ${label.toLowerCase()}`}</option>
          ))}
        </select>
      ))}
    </div>
  );
}

function DrillCard({ drill, theme, showAssign, onAssign }) {
  const diffColor = {
    easy: '#00C9A7', moderate: '#00D9FF', hard: '#fbbf24',
  }[drill.difficulty] || '#94a3b8';
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(150,160,180,0.2)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h3 className="font-bold leading-tight">{drill.name}</h3>
        <span
          className="text-[10px] uppercase tracking-widest font-bold shrink-0 px-2 py-0.5 rounded"
          style={{ background: `${diffColor}22`, color: diffColor, border: `1px solid ${diffColor}55` }}
        >
          {drill.difficulty}
        </span>
      </div>
      <p className="text-sm opacity-80 mb-3">{drill.description}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(drill.focus_tags || []).map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                style={{ background: 'rgba(0,201,167,0.12)', color: '#00C9A7' }}>
            {t.replace(/_/g, ' ')}
          </span>
        ))}
      </div>
      <div className="text-xs opacity-70 mb-3">
        {drill.role && <>{drill.role} · </>}
        {drill.default_sets}×{drill.default_reps}
        {drill.duration_minutes && <> · {drill.duration_minutes} min</>}
      </div>
      {showAssign && (
        <button
          onClick={onAssign}
          className="w-full py-2 rounded-lg font-semibold text-sm"
          style={{
            background: 'rgba(0,201,167,0.12)',
            color: '#00C9A7',
            border: '1px solid rgba(0,201,167,0.35)',
          }}
        >
          Assign to athlete
        </button>
      )}
    </div>
  );
}

function CreateDrillModal({ token, onClose, theme }) {
  const [body, setBody] = useState({
    name: '', description: '', focus_tags: '',
    role: 'Batter', difficulty: 'moderate',
    default_sets: 3, default_reps: 10, duration_minutes: 15,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!body.name || !body.description) {
      toast.error('Name + description required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/drills`, {
        ...body,
        focus_tags: body.focus_tags.split(',').map((s) => s.trim()).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Drill created');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create drill');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New drill">
      <Field label="Name" value={body.name} onChange={(v) => setBody({ ...body, name: v })} />
      <Field label="Description" value={body.description} onChange={(v) => setBody({ ...body, description: v })} multiline />
      <Field label="Focus tags (comma-separated)"
             value={body.focus_tags} onChange={(v) => setBody({ ...body, focus_tags: v })}
             placeholder="cover_drive, batting_basics" />
      <div className="grid grid-cols-3 gap-2">
        <NumField label="Sets" value={body.default_sets} onChange={(v) => setBody({ ...body, default_sets: v })} />
        <NumField label="Reps" value={body.default_reps} onChange={(v) => setBody({ ...body, default_reps: v })} />
        <NumField label="Minutes" value={body.duration_minutes} onChange={(v) => setBody({ ...body, duration_minutes: v })} />
      </div>
      <button onClick={submit} disabled={busy}
              className="w-full py-2.5 rounded-lg font-semibold text-white mt-2"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}>
        {busy ? 'Creating…' : 'Create drill'}
      </button>
    </Modal>
  );
}

function AssignTaskModal({ drill, token, onClose, theme }) {
  const [athleteId, setAthleteId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/coach/students`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setStudents(r.data?.students || []))
      .catch(() => setStudents([]));
  }, [token]);

  const submit = async () => {
    if (!athleteId) {
      toast.error('Pick an athlete');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/coach/tasks`, {
        athlete_id: athleteId,
        drill_id: drill.id,
        notes,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Task assigned');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not assign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Assign: ${drill.name}`}>
      <div>
        <label className="block text-sm font-medium opacity-80 mb-1.5">Athlete</label>
        <select
          value={athleteId}
          onChange={(e) => setAthleteId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
        >
          <option value="">— pick —</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>{s.full_name || s.id}</option>
          ))}
        </select>
        {students.length === 0 && (
          <p className="text-xs opacity-60 mt-1">
            No students yet — invite a parent to add their athlete to your household.
          </p>
        )}
      </div>
      <Field label="Due date (optional)" type="date" value={dueAt} onChange={setDueAt} />
      <Field label="Notes (optional)" value={notes} onChange={setNotes} multiline />
      <button onClick={submit} disabled={busy}
              className="w-full py-2.5 rounded-lg font-semibold text-white mt-2"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}>
        {busy ? 'Assigning…' : 'Assign task'}
      </button>
    </Modal>
  );
}

// ---------- atoms ----------

function Modal({ onClose, title, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl p-5 max-w-md w-full space-y-3"
        style={{ background: '#0a0e27', color: '#fff', border: '1px solid rgba(0,201,167,0.3)' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, type = 'text', ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium opacity-80 mb-1.5">{label}</label>
      {multiline ? (
        <textarea
          value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full px-3 py-2 rounded-lg resize-none"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
          {...rest}
        />
      ) : (
        <input
          value={value} onChange={(e) => onChange(e.target.value)} type={type}
          className="w-full px-3 py-2 rounded-lg"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
          }}
          {...rest}
        />
      )}
    </div>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <Field label={label} type="number"
           value={value}
           onChange={(v) => onChange(parseInt(v) || 0)} />
  );
}
